import { Injectable } from '@angular/core';
import { _find } from '@core/lodash/lodash';
import { ScheduleItemFilterStatus, ScheduleItemType } from '@gandalf/constants';
import { AppointmentResponse } from '@gandalf/model/appointment-response';
import { AppointmentSlotResponse } from '@gandalf/model/appointment-slot-response';
import { BusyTimeResponse } from '@gandalf/model/busy-time-response';
import { EmployeeOfficeHoursResponse } from '@gandalf/model/employee-office-hours-response';
import { OfficeClosedResponse } from '@gandalf/model/office-closed-response';
import { PatientDashboardSummaryResponse } from '@gandalf/model/patient-dashboard-summary-response';
import { PersonNameResponse } from '@gandalf/model/person-name-response';
import { ProviderResponse } from '@gandalf/model/provider-response';
import { ScheduleItemResponse } from '@gandalf/model/schedule-item-response';
import { PatientGandalfService, ProviderGandalfService, ScheduleGandalfService } from '@gandalf/services';
import { EmployeeNamePipe } from '@shared/pipes/employee-dropdown-name/employee-name.pipe';
import { PatientNamePipe } from '@shared/pipes/patient-name/patient-name.pipe';
import { PersonAgePipe } from '@shared/pipes/person-age/person-age.pipe';
import { ProviderNamePipe } from '@shared/pipes/provider-name/provider-name.pipe';
import { Observable } from 'rxjs';
import { ScheduleItemCalendarResponse } from '@gandalf/model/schedule-item-calendar-response';
import { OfficeClosedCalendarResponse } from '@gandalf/model/office-closed-calendar-response';
import { EmployeeOfficeHoursCalendarResponse } from '@gandalf/model/employee-office-hours-calendar-response';
import { BusyTimeCalendarResponse } from '@gandalf/model/busy-time-calendar-response';
import { AppointmentSlotCalendarResponse } from '@gandalf/model/appointment-slot-calendar-response';
import { AppointmentCalendarResponse } from '@gandalf/model/appointment-calendar-response';
import { ScheduleItemSidebarResponse } from '@gandalf/model/schedule-item-sidebar-response';
import { AppointmentSlotSidebarResponse } from '@gandalf/model/appointment-slot-sidebar-response';
import { AppointmentSidebarResponse } from '@gandalf/model/appointment-sidebar-response';
import { BusyTimeSidebarResponse } from '@gandalf/model/busy-time-sidebar-response';
import { EmployeeOfficeHoursSidebarResponse } from '@gandalf/model/employee-office-hours-sidebar-response';
import { OfficeClosedSidebarResponse } from '@gandalf/model/office-closed-sidebar-response';

export type ScheduleItemTypes =
	ScheduleItemResponse |
	AppointmentResponse |
	AppointmentSlotResponse |
	OfficeClosedResponse |
	BusyTimeResponse |
	EmployeeOfficeHoursResponse;

export type ScheduleItemCalendarTypes =
	ScheduleItemCalendarResponse |
	AppointmentCalendarResponse |
	AppointmentSlotCalendarResponse |
	OfficeClosedCalendarResponse |
	BusyTimeCalendarResponse |
	EmployeeOfficeHoursCalendarResponse;

export type ScheduleItemSidebarTypes =
	ScheduleItemSidebarResponse |
	AppointmentSidebarResponse |
	AppointmentSlotSidebarResponse |
	BusyTimeSidebarResponse |
	EmployeeOfficeHoursSidebarResponse |
	OfficeClosedSidebarResponse;

export interface AssigneeDetail {
	id: string;
	name?: string;
	color?: number;
	officeHoursScheduleItems?: EmployeeOfficeHoursResponse[];
}

export interface AssigneeCalendarDetail {
	id: string;
	name?: string;
	color?: number;
	workDays?: number[];
	officeHoursScheduleItems?: EmployeeOfficeHoursCalendarResponse[];
}

export const SCHEDULE_ITEM_FILTER_DEFAULT_ITEM_TYPES =
	[ScheduleItemType.APPOINTMENT, ScheduleItemType.BUSY, ScheduleItemType.OFFICE_CLOSED, ScheduleItemType.EMPLOYEE_HOURS, ScheduleItemType.APPOINTMENT_SLOT];

export const SCHEDULE_ITEM_FILTER_DEFAULT_STATUSES =
	[ScheduleItemFilterStatus.ACTIVE, ScheduleItemFilterStatus.COMPLETED, ScheduleItemFilterStatus.IN_PROGRESS, ScheduleItemFilterStatus.AVAILABLE_SLOTS];

@Injectable({
	providedIn: 'root',
})
export class ScheduleItemUtilService {
	PERSON_ID_TAG = 'person-';
	ROLE_ID_TAG = 'userRole-';
	CLOSED_ID_TAG = 'closed-';
	scheduleItemTypes = ScheduleItemType;
	BLACK_DISPLAY_COLOR = 0;
	practiceProviders: ProviderResponse[];

	constructor(
		private employeeNamePipe: EmployeeNamePipe,
		private providerNamePipe: ProviderNamePipe,
		private personAgePipe: PersonAgePipe,
		private patientNamePipe: PatientNamePipe,
		private providerGandalfService: ProviderGandalfService,
		private patientGandalfService: PatientGandalfService,
		private scheduleGandalfService: ScheduleGandalfService,
	) {
		this.getPracticeProviders();
	}

	getPatientById(patientId: number): Observable<PatientDashboardSummaryResponse> {
		return this.patientGandalfService.getPatientDashboardSummaryById(patientId);
	}

	getPersonIdByPatientId(patientId: number): Observable<PersonNameResponse> {
		return this.patientGandalfService.getPatientPersonId(patientId);
	}

	getPracticeProviders() {
		this.providerGandalfService.findPracticeProviders().subscribe((data) => {
			this.practiceProviders = data;
		});
	}

	formatAssigneeDetail(scheduleItem: ScheduleItemTypes): AssigneeDetail {
		const assigneeDetail: AssigneeDetail = {
			name: '',
			id: '',
			officeHoursScheduleItems: [],
		};

		this.buildAssigneeDetail(scheduleItem, assigneeDetail);

		return assigneeDetail;
	}

	formatAssigneeCalendarDetail(scheduleItem: ScheduleItemCalendarTypes): AssigneeCalendarDetail {
		const assigneeDetail: AssigneeCalendarDetail = {
			name: '',
			id: '',
			officeHoursScheduleItems: [],
			workDays: [],
		};

		this.buildAssigneeDetail(scheduleItem, assigneeDetail);

		return assigneeDetail;
	}

	private buildAssigneeDetail(scheduleItem: ScheduleItemCalendarTypes | ScheduleItemTypes, assigneeDetail: AssigneeDetail | AssigneeCalendarDetail) {
		if ((scheduleItem as AppointmentResponse | AppointmentCalendarResponse).provider) {
			const personId = (scheduleItem as AppointmentResponse | AppointmentCalendarResponse).provider.personId;
			assigneeDetail.name = this.providerNamePipe.transform((scheduleItem as AppointmentResponse | AppointmentCalendarResponse).provider);
			assigneeDetail.id = `${this.PERSON_ID_TAG}${personId}`;
			assigneeDetail.color = this.getAssigneeDetailColor(personId);
		} else if (scheduleItem.employee) {
			const personId = scheduleItem.employee.personId;
			assigneeDetail.name = this.employeeNamePipe.transform(scheduleItem.employee);
			assigneeDetail.id = `${this.PERSON_ID_TAG}${personId}`;
			assigneeDetail.color = this.getAssigneeDetailColor(personId);
		} else if ((scheduleItem instanceof ScheduleItemResponse) && scheduleItem.role) {
			assigneeDetail.name = scheduleItem.role.name;
			assigneeDetail.id = `${this.ROLE_ID_TAG}${scheduleItem.role.id}`;
		} else if ((scheduleItem instanceof ScheduleItemCalendarResponse) && scheduleItem.roleId) {
			assigneeDetail.name = scheduleItem.roleName;
			assigneeDetail.id = `${this.ROLE_ID_TAG}${scheduleItem.roleId}`;
		} else if (scheduleItem.type === this.scheduleItemTypes.OFFICE_CLOSED) {
			assigneeDetail.name = 'Office';
			assigneeDetail.id = this.CLOSED_ID_TAG;
		}
	}

	getAssigneeDetailColor(personId: number): number {
		const provider = _find(this.practiceProviders, ['personId', personId]);
		return provider ? provider.providerDisplayColor : this.BLACK_DISPLAY_COLOR;
	}

	getPatientAgeInYears(scheduleItem: ScheduleItemTypes | ScheduleItemCalendarTypes) {
		return this.personAgePipe.transform((scheduleItem as AppointmentResponse | AppointmentCalendarResponse).patientDob);
	}

	getPatientFullName(scheduleItem: ScheduleItemTypes | ScheduleItemCalendarTypes) {
		return this.patientNamePipe.transform((scheduleItem as AppointmentResponse | AppointmentCalendarResponse).patientName);
	}

	getPatientSchedulingPreferences(patientId: number) {
		return this.scheduleGandalfService.findSchedulingPreferencesByPatientId(patientId);
	}

	getPatientSchedulingInfo(patientId: number) {
		return this.scheduleGandalfService.findPatientSchedulingInfoByPatientId(patientId);
	}
}
